import {
	Graph,
	Dnd,
	Transform,
	Snapline,
	History,
	Selection,
	MiniMap,
	BackgroundManagerOptions,
} from '@antv/x6';
import {registerArchimateShapes} from '../shapes/registerArchimateShapes';
import {getEmbeddingFindParent, getEmbeddingValidate} from '@core_x6/lib/node-types';
import {
	MIN_ELEMENT_WIDTH,
	getMinElementHeightForShape,
	buildX6RouterConfig,
} from '../modelerX6/constants';
import {canvasHistoryBeforeAddCommand} from './canvasHistoryFilter';
import ViewerX6 from '../ViewerX6';

type ValidateConnectionFunc = () => boolean;

export interface IGraphOptions {
	getValidateConnection?: (context: ViewerX6) => ValidateConnectionFunc;
	background?: false | BackgroundManagerOptions;
	connectionRouter?: 'normal' | 'orth' | 'manhattan';
	minimapContainer?: HTMLElement;
	readOnly?: boolean;
}

export default class GraphX6 {
	_container: HTMLElement | null;
	_graph: Graph | null;
	_options: IGraphOptions;
	_minimap: MiniMap | null;
	_dnd: Dnd | null;
	_selection: Selection | null;

	constructor(options: IGraphOptions = {}) {
		this._options = options;
		this._graph = null;
		this._container = null;
		this._minimap = null;
		this._dnd = null;
		this._selection = null;
	}

	init(container: HTMLElement, context: ViewerX6) {
		this._container = container;

		registerArchimateShapes();

		const readOnly = Boolean(this._options.readOnly);
		const validateConnection =
			context && typeof this._options.getValidateConnection === 'function'
				? this._options.getValidateConnection(context)
				: null;

		this._graph = new Graph({
			container: this._container,
			autoResize: true,
			// Рёбра рисуем поверх узлов: сортировка по zIndex + у рёбер zIndex выше
			// sorting: 'exact',
			grid: {
				size: 10,
				visible: true,
			},
			// Свайп двумя пальцами (trackpad) — пан; зум только с Ctrl/Cmd + колёсико
			panning: {
				enabled: true,
				eventTypes: ['leftMouseDown', 'rightMouseDown', 'mouseWheel'],
			},
			mousewheel: {
				enabled: true,
				factor: 1.08,
				modifiers: ['ctrl', 'meta'],
			},
			background: this._options.background,
			connecting: {
				allowBlank: false,
				allowNode: !readOnly,
				allowLoop: false,
				validateConnection: () => {
					if (typeof validateConnection === 'function') {
						return validateConnection();
					} else {
						return true;
					}
				},
				anchor: 'orth',
				connectionPoint: 'boundary',
				connector: {name: 'jumpover', args: {size: 6, type: 'arc'}},
				router: buildX6RouterConfig(
					// context?.getConnectionRouter?.() ??
					this._options.connectionRouter ?? 'normal'
				),
			},
			// Вложенность: при перетаскивании на узел с canBeParent === true элемент становится дочерним
			embedding: {
				enabled: !readOnly,
				findParent: getEmbeddingFindParent(),
				frontOnly: true,
				validate: getEmbeddingValidate(),
			},
			interacting: readOnly
				? false
				: {
						edgeLabelMovable: true,
						// Иначе drag по вложенной ноде двигает родителя (getDelegatedView), embedding/unembed не работают.
						stopDelegateOnDragging: true,
					},
			/**
			 * По умолчанию в X6 async: true — размонтирование CellView откладывается (rAF).
			 * В renderView после clearCells() мы сразу создаём новые узлы; при async старый
			 * SVG иногда остаётся в stage (курсор как над узлом, выделить нельзя). Синхронный
			 * flush рендера убирает эту гонку.
			 */
			async: false,
		});

		this._dnd = readOnly
			? null
			: new Dnd({
					target: this._graph,
					scaled: true,
				});

		// Ресайз узлов при клике (плагин Transform). Для junction (And/Or) ресайз отключён
		if (!readOnly) {
			const JUNCTION_SHAPES = ['archimate:and-junction', 'archimate:or-junction'];
			this._graph.use(
				new Transform({
					resizing: {
						enabled: node => {
							const shape: string =
								'getShape' in node && typeof node.getShape === 'function'
									? node.getShape()
									: node.shape;

							return !JUNCTION_SHAPES.includes(shape);
						},
						minWidth: MIN_ELEMENT_WIDTH,
						minHeight: node => {
							const shape: string =
								'getShape' in node && typeof node.getShape === 'function'
									? node.getShape()
									: node.shape;

							return getMinElementHeightForShape(shape);
						},
					},
				})
			);
		}

		// Snapline — направляющие при перемещении узлов
		this._graph.use(
			new Snapline({
				enabled: true,
				tolerance: 10,
				resizing: true,
			})
		);

		// History включается только после полного renderView (см. renderView.js); до этого откаты не накапливаются
		this._graph.use(
			new History({
				enabled: false,
				beforeAddCommand: canvasHistoryBeforeAddCommand,
			})
		);

		// rubberband: false — рамка на пустом месте только по кнопке палитры (иначе визуально «включено», а кнопка выкл → нужны 2 клика)
		// showNodeSelectionBox: false — HTML-оверлей Selection перехватывает mousedown и двигает через
		// node.translate (batch move-selection), минуя NodeView.dragNode → embedding/parentNodeId не работают.
		this._selection = new Selection({
			enabled: true,
			multiple: true,
			multipleSelectionModifiers: ['ctrl', 'meta'],
			rubberband: false,
			movable: !readOnly,
			showNodeSelectionBox: false,
			showEdgeSelectionBox: false,
		});
		this._graph.use(this._selection);

		// MiniMap — мини-карта (если передан контейнер)
		const minimapContainer = this._options.minimapContainer;
		if (minimapContainer) {
			this._minimap = new MiniMap({
				container: minimapContainer,
				width: 200,
				height: 150,
				padding: 10,
				/* Как у основного графа: иначе после clearCells + bulk-add «хвосты» остаются в SVG миникарты */
				graphOptions: {async: false},
			});
			this._graph.use(this._minimap);
		}

		return this;
	}

	destroy() {
		this._selection = null;
		this._minimap = null;
		if (this._graph) {
			this._graph.dispose();
			this._graph = null;
		}
	}

	getGraph() {
		return this._graph;
	}

	getDnd() {
		return this._dnd;
	}

	getSelection() {
		return this._selection;
	}
}
